'use client'

import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { motion } from 'motion/react'

import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'

import { TweetCard } from './TweetCard'

import type { TweetData, TweetMedia, TweetUser } from './TweetCard'

type TimelineResponse = {
  data: TweetData[]
  includes: {
    users?: TweetUser[]
    media?: TweetMedia[]
  }
  meta: {
    next_token: string | null
    result_count: number
  }
}

function mergeIncludes(
  a: NonNullable<TimelineResponse['includes']>,
  b: NonNullable<TimelineResponse['includes']>,
): NonNullable<TimelineResponse['includes']> {
  const userMap = new Map((a.users ?? []).map((u) => [u.id, u]))
  for (const u of b.users ?? []) userMap.set(u.id, u)
  const mediaMap = new Map((a.media ?? []).map((m) => [m.media_key, m]))
  for (const m of b.media ?? []) mediaMap.set(m.media_key, m)
  return { users: [...userMap.values()], media: [...mediaMap.values()] }
}

const getTimeline = async (nextToken?: string | null): Promise<TimelineResponse> => {
  const qs = nextToken ? `?next_token=${encodeURIComponent(nextToken)}` : ''
  const res = await fetch(`/api/x-timeline${qs}`)
  const json = (await res.json()) as {
    data?: TweetData[]
    includes?: TimelineResponse['includes']
    error?: string
    meta?: { next_token: string | null; result_count: number }
  }
  if (!res.ok) {
    throw new Error(json.error ?? 'Failed to load timeline')
  }
  return {
    data: json.data ?? [],
    includes: json.includes ?? {},
    meta: {
      next_token: json.meta?.next_token ?? null,
      result_count: json.meta?.result_count ?? json.data?.length ?? 0,
    },
  }
}

export const XTimeline = () => {
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  useEffect(() => {
    const fetchTimeline = async () => {
      setLoading(true)
      try {
        const data = await getTimeline()
        setTimeline(data)
      } catch {
        setTimeline(null)
      } finally {
        setLoading(false)
      }
    }
    fetchTimeline()
  }, [])

  const loadMore = useCallback(async () => {
    setLoadingMore(true)
    try {
      setTimeline((prev) => {
        if (!prev || !prev.meta.next_token || loading || loadingMore) {
          setLoadingMore(false)
          return prev
        }
        getTimeline(prev.meta.next_token)
          .then((page) => {
            setTimeline((curr) => {
              if (!curr) return page
              const seen = new Set(curr.data.map((t) => t.id))
              const moreTweets = page.data.filter((t) => !seen.has(t.id))
              return {
                data: [...curr.data, ...moreTweets],
                includes: mergeIncludes(curr.includes, page.includes),
                meta: page.meta,
              }
            })
          })
          .catch(() => {})
          .finally(() => setLoadingMore(false))
        return prev
      })
    } catch {
      setLoadingMore(false)
    }
  }, [loading, loadingMore])

  const userMap = new Map(timeline?.includes.users?.map((u) => [u.id, u]))
  const mediaMap = new Map(timeline?.includes.media?.map((m) => [m.media_key, m]))

  return (
    <motion.div
      initial={false}
      animate={{ maxHeight: '800rem' }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="mb-2 flex w-full min-w-0 flex-col gap-8 overflow-clip rounded-2xl border border-border bg-background p-4 lg:p-6"
    >
      <div className="flex flex-col gap-2">
        <h2 className="flex flex-row items-center font-body text-xl font-medium text-foreground">
          <Media
            resource="/media/logo_x.svg"
            context={'thumbnail'}
            alt="X"
            width={40}
            height={40}
          />
          Latest Updates
        </h2>
        {loading && (
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
            <Loader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">Loading</p>
          </div>
        )}
        {!loading && timeline ? (
          <>
            <div className="columns-1 gap-4 md:columns-2 xl:columns-3">
              {timeline.data.map((tweet) => {
                const author = tweet.author_id ? userMap.get(tweet.author_id) : undefined
                const tweetMedia = tweet.attachments?.media_keys
                  ?.map((key) => mediaMap.get(key))
                  .filter((m): m is TweetMedia => !!m)

                return <TweetCard key={tweet.id} tweet={tweet} author={author} media={tweetMedia} />
              })}
            </div>
            {timeline.meta.next_token ? (
              <div className="flex flex-row justify-end pt-2">
                <Button
                  variant="accent"
                  color="default"
                  icon={false}
                  disabled={loadingMore}
                  onClick={() => void loadMore()}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex min-h-[30vh] flex-col items-center justify-center gap-3">
            <p className="text-sm text-muted-foreground">X timeline unavailable</p>
          </div>
        )}
      </div>
    </motion.div>
  )
}
